import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../../i18n/LanguageProvider';

interface Props {
	cardId: string;
	cardTitle?: string;
}

/**
 * Share the card via WhatsApp, copy link, email, X, or the native share sheet.
 * Shares a clean public card link (without any recipient-specific query params).
 * Roadmap: "Send cards via WhatsApp" + "Create a public share link".
 */
const ShareButton: React.FC<Props> = ({ cardId, cardTitle }) => {
	const { t } = useT();
	const [open, setOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const shareUrl = `${window.location.origin}/viewcard/${cardId}`;
	const shareText = cardTitle ? `${cardTitle} 💌` : 'I made you a card 💌';
	const canNativeShare = typeof navigator !== 'undefined' && !!(navigator as any).share;

	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', onClick);
		return () => document.removeEventListener('mousedown', onClick);
	}, []);

	const openWindow = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

	const shareWhatsApp = () => openWindow(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`);
	const shareEmail = () =>
		openWindow(`mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`);
	const shareX = () =>
		openWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`);

	const copyLink = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl);
		} catch {
			const ta = document.createElement('textarea');
			ta.value = shareUrl;
			document.body.appendChild(ta);
			ta.select();
			document.execCommand('copy');
			document.body.removeChild(ta);
		}
		setCopied(true);
		setTimeout(() => setCopied(false), 1800);
	};

	const nativeShare = async () => {
		try {
			await (navigator as any).share({ title: shareText, text: shareText, url: shareUrl });
			setOpen(false);
		} catch {
			/* user cancelled */
		}
	};

	const itemClass =
		'flex items-center gap-2 w-full text-left px-4 py-2.5 border-none bg-transparent text-sm text-slate-700 cursor-pointer hover:bg-slate-50 transition-colors duration-100';

	return (
		<div ref={dropdownRef} className="relative inline-block">
			<button
				className="flex items-center gap-2 px-4 py-2.5 bg-white bg-opacity-90 backdrop-blur-sm text-slate-700 border border-slate-200 rounded-xl font-semibold text-sm cursor-pointer shadow-lg hover:bg-opacity-100 hover:shadow-xl transition-all duration-200"
				onClick={() => setOpen((o) => !o)}
			>
				<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
					<path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
				</svg>
				{t('common.share')}
				<svg className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
					<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
				</svg>
			</button>
			{open && (
				<div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl overflow-hidden z-50 min-w-[220px] border border-slate-100">
					<button className={itemClass} onClick={shareWhatsApp}>
						<svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 24 24">
							<path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
						</svg>
						{t('viewcard.shareToWhatsapp')}
					</button>
					<button className={itemClass} onClick={copyLink}>
						<svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
							<path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
						</svg>
						{copied ? t('viewcard.linkCopied') : t('viewcard.copyLink')}
					</button>
					<button className={itemClass} onClick={shareEmail}>
						<svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
							<path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
						</svg>
						{t('viewcard.shareViaEmail')}
					</button>
					<button className={itemClass} onClick={shareX}>
						<svg className="w-4 h-4 text-slate-700" fill="currentColor" viewBox="0 0 24 24">
							<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
						</svg>
						{t('viewcard.shareOnX')}
					</button>
					{canNativeShare && (
						<>
							<div className="border-t border-slate-100" />
							<button className={itemClass} onClick={nativeShare}>
								<svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
									<path strokeLinecap="round" strokeLinejoin="round" d="M12 4v12m0-12l-4 4m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
								</svg>
								More…
							</button>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default ShareButton;
